import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { LoginForm } from "@app/components/organisms/login-form";
import {
  getDefaultPathForRole,
  resolvePostAuthPath,
} from "@app/lib/auth/navigation";
import { getRoleLabelFromJwtRoles } from "@app/lib/role-utils";
import { useJwtAuth } from "@app/providers/jwt-auth-provider";

type LoginLocationState = {
  banner?: string;
};

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useJwtAuth();

  // Snapshot any one-shot banner (e.g. "Email verified \u2014 please sign in.")
  // passed via location.state, then clear it so a refresh / back-nav doesn't
  // re-show it.
  const [banner] = useState<string>(() => {
    const state = location.state as LoginLocationState | null;
    return state?.banner ?? "";
  });
  useEffect(() => {
    const state = location.state as LoginLocationState | null;
    if (state?.banner) {
      window.history.replaceState({ ...window.history.state, usr: null }, "");
    }
    // Only run on mount — banner is a one-shot signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSuccess = useCallback(() => {
    const roleLabel = getRoleLabelFromJwtRoles(user?.roles);
    const defaultPath = getDefaultPathForRole(roleLabel);
    const redirectTo = resolvePostAuthPath(location, defaultPath, roleLabel);
    navigate(redirectTo, { replace: true });
  }, [location, navigate, user?.roles]);

  const handleSwitchToSignup = useCallback(() => {
    navigate("/signup");
  }, [navigate]);

  return (
    <section className="events-page events-auth-page">
      <div className="events-auth-card events-auth-card--page">
        {banner ? (
          <p
            className="events-auth-card__notice"
            role="status"
            aria-live="polite"
          >
            {banner}
          </p>
        ) : null}
        <LoginForm
          onSuccess={handleSuccess}
          onSwitchToSignup={handleSwitchToSignup}
          headingAs="h1"
        />
      </div>
    </section>
  );
}
